import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Collapse,
  Divider,
  Chip,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAllFeedbacks,
  createFeedback,
  updateFeedback,
  deleteFeedback,
} from "../../../Services/Services";

const ratingOptions = [
  { value: 1, label: "Very Poor" },
  { value: 2, label: "Poor" },
  { value: 3, label: "Average" },
  { value: 4, label: "Good" },
  { value: 5, label: "Excellent" },
];

const RoleManagementTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    company_name: "",
    feedback: "",
    rating: 3,
    is_resolved: false,
    division: storedDivision?.division_id || ""
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const fetchFeedbacks = async () => {
    try {
      const data = await getAllFeedbacks();
      setRows(data);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const success = await deleteFeedback(id);
      if (success) {
        setRows((prev) => prev.filter((r) => r.feedback_id !== id));
        toast.success("Feedback deleted successfully!");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete feedback!");
    }
  };

  const handleAddNewRow = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user_id"));
      const formDataWithUser = {
        ...formData,
        created_by: user,
      };

      const data = await createFeedback(formDataWithUser);

      setRows([...rows, data]);

      setFormData({
        client_name: "",
        client_email: "",
        company_name: "",
        feedback: "",
        rating: 3,
        is_resolved: false,
        division: storedDivision?.division_id || ""
      });

      setOpenAddModal(false);
      toast.success("Feedback submitted successfully!");
    } catch (error) {
      console.error("Add failed:", error);
      toast.error("Failed to submit feedback!");
    }
  };

  const handleEdit = (id) => {
    const record = rows.find((r) => r.feedback_id === id);
    if (record) {
      setFormData({ ...record });
      setEditId(id);
      setOpenEditModal(true);
    }
  };

  const handleUpdateRow = async () => {
    try {
      const updated = await updateFeedback(editId, formData);
      setRows((prev) =>
        prev.map((row) => (row.feedback_id === editId ? updated : row))
      );
      setOpenEditModal(false);
      setEditId(null);
      toast.success("Feedback updated successfully!");
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update feedback!");
    }
  };

  const getFilteredSortedRows = () => {
    let filtered = rows.filter((row) =>
      row.client_name.toLowerCase().includes(filter.toLowerCase())
    );
    filtered.sort((a, b) => {
      return sortAsc
        ? a.client_name.localeCompare(b.client_name)
        : b.client_name.localeCompare(a.client_name);
    });
    return filtered;
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 5:
        return "#4caf50";
      case 4:
        return "#8bc34a";
      case 3:
        return "#ff9800";
      case 2:
        return "#ff5722";
      case 1:
        return "#f44336";
      default:
        return "#757575";
    }
  };

  const filteredRows = getFilteredSortedRows();

  return (
    <Box sx={{ px: 0, pt: 0, mt: 3, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* 🔍 FILTERS */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Search by client name"
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <Typography
              sx={{
                border: "1px solid #ccc",
                px: 2,
                py: 0.5,
                borderRadius: 0,
                fontSize: "0.7rem",
                height: 32,
                display: "flex",
                alignItems: "center",
                bgcolor: "#f5f5f5",
              }}
            >
              Total: {filteredRows.length}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setSortAsc(!sortAsc)}
              sx={{
                height: 32,
                fontSize: "0.65rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              Sort ({sortAsc ? "A-Z" : "Z-A"})
            </Button>
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => setOpenAddModal(true)}
              startIcon={<AddIcon />}
              sx={{
                height: 32,
                fontSize: "0.7rem",
                borderRadius: 0,
              }}
            >
              Feedback
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* 📱 MOBILE VIEW (CARDS) */}
      {isMobile ? (
        <Box sx={{ bgcolor: "background.paper" }}>
          {filteredRows.map((row, index) => (
            <Box key={row.feedback_id}>
              <Box
                onClick={() =>
                  setExpandedFeedback(
                    expandedFeedback === row.feedback_id
                      ? null
                      : row.feedback_id
                  )
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  "&:active": {
                    bgcolor: "#e3f2fd",
                  },
                  borderLeft: `4px solid ${getRatingColor(row.rating)}`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#666",
                        minWidth: 25,
                      }}
                    >
                      #{index + 1}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        flex: 1,
                      }}
                    >
                      {row.client_name}
                    </Typography>
                    <Chip
                      label={
                        ratingOptions.find((opt) => opt.value === row.rating)
                          ?.label
                      }
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: getRatingColor(row.rating),
                        color: "#fff",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>
                </Box>

                <IconButton size="small">
                  {expandedFeedback === row.feedback_id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse
                in={expandedFeedback === row.feedback_id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                    borderLeft: `4px solid ${getRatingColor(row.rating)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        EMAIL
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {row.client_email}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        FEEDBACK
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "#555",
                          lineHeight: 1.4,
                        }}
                      >
                        {row.feedback}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 0.6,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEdit(row.feedback_id);
                      }}
                      sx={{
                        bgcolor: "#e3f2fd",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#bbdefb",
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: "1rem", color: "#1976d2" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setAnchorEl(event.currentTarget);
                        setProjectToDelete(row.feedback_id);
                      }}
                      sx={{
                        bgcolor: "#ffebee",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "1rem", color: "#d32f2f" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Collapse>

              <Divider />
            </Box>
          ))}
        </Box>
      ) : (
        /* 💻 DESKTOP TABLE VIEW (ORIGINAL) */
        <TableContainer
          component={Paper}
          sx={{ maxHeight: "88vh", overflow: "auto" }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1600,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: "1px solid #ccc",
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& th": {
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                    color: "#333",
                    borderBottom: "1px solid #999",
                    backgroundColor: "#f5f5f5",
                    fontSize: "0.8rem",
                  },
                }}
              >
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#f5f5f5",
                    zIndex: 3,
                  }}
                >
                  S. No.
                </TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Resolved</TableCell>
                <TableCell
                  sx={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#f5f5f5",
                    zIndex: 3,
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row, index) => (
                <TableRow
                  hover
                  key={index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    paddingRight: "13px !important",
                    paddingLeft: "13px !important",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                      "& td": {
                        backgroundColor: "#e3f2fd !important",
                      },
                    },
                    "& td": {
                      fontSize: "0.75rem",
                      padding: "0px",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#fafafa",
                      zIndex: 2,
                    }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell>{row.client_name}</TableCell>
                  <TableCell>{row.client_email}</TableCell>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell>{row.feedback}</TableCell>
                  <TableCell>
                    {
                      ratingOptions.find((opt) => opt.value === row.rating)
                        ?.label
                    }
                  </TableCell>
                  <TableCell>{row.is_resolved ? "Yes" : "No"}</TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#fff",
                      zIndex: 2,
                    }}
                  >
                    <IconButton
                      sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                      onClick={() => handleEdit(row.feedback_id)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon transform="scale(0.6)" />
                    </IconButton>

                    <IconButton
                      sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                      color="error"
                      size="small"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setProjectToDelete(row.feedback_id);
                      }}
                    >
                      <DeleteIcon transform="scale(0.6)" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ➕ ADD FEEDBACK DIALOG */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAddNewRow();
          }}
        >
          <DialogTitle>Add Client Feedback</DialogTitle>
          <DialogContent>
            <TextField
              label="Client Name"
              fullWidth
              required
              margin="dense"
              value={formData.client_name}
              onChange={(e) =>
                setFormData({ ...formData, client_name: e.target.value })
              }
            />
            <TextField
              label="Client Email"
              fullWidth
              required
              type="email"
              margin="dense"
              value={formData.client_email}
              onChange={(e) =>
                setFormData({ ...formData, client_email: e.target.value })
              }
            />
            <TextField
              label="Company Name"
              fullWidth
              required
              margin="dense"
              value={formData.company_name}
              onChange={(e) =>
                setFormData({ ...formData, company_name: e.target.value })
              }
            />
            <TextField
              label="Feedback"
              fullWidth
              required
              multiline
              rows={3}
              margin="dense"
              value={formData.feedback}
              onChange={(e) =>
                setFormData({ ...formData, feedback: e.target.value })
              }
            />
            <Select
              fullWidth
              value={formData.rating}
              onChange={(e) =>
                setFormData({ ...formData, rating: e.target.value })
              }
              margin="dense"
            >
              {ratingOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddModal(false)} color="error">
              Close
            </Button>
            <Button type="submit" color="primary">
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ✏️ EDIT FEEDBACK DIALOG */}
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdateRow();
          }}
        >
          <DialogTitle>Edit Feedback</DialogTitle>
          <DialogContent>
            <TextField
              label="Client Name"
              fullWidth
              required
              margin="dense"
              value={formData.client_name}
              onChange={(e) =>
                setFormData({ ...formData, client_name: e.target.value })
              }
            />
            <TextField
              label="Client Email"
              fullWidth
              required
              type="email"
              margin="dense"
              value={formData.client_email}
              onChange={(e) =>
                setFormData({ ...formData, client_email: e.target.value })
              }
            />
            <TextField
              label="Company Name"
              fullWidth
              required
              margin="dense"
              value={formData.company_name}
              onChange={(e) =>
                setFormData({ ...formData, company_name: e.target.value })
              }
            />
            <TextField
              label="Feedback"
              fullWidth
              required
              multiline
              rows={3}
              margin="dense"
              value={formData.feedback}
              onChange={(e) =>
                setFormData({ ...formData, feedback: e.target.value })
              }
            />
            <Select
              fullWidth
              value={formData.rating}
              onChange={(e) =>
                setFormData({ ...formData, rating: e.target.value })
              }
              margin="dense"
            >
              {ratingOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_resolved}
                  onChange={(e) =>
                    setFormData({ ...formData, is_resolved: e.target.checked })
                  }
                />
              }
              label="Resolved"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditModal(false)} color="error">
              Close
            </Button>
            <Button type="submit" color="primary">
              Update
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ❌ DELETE CONFIRMATION */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                await handleDelete(projectToDelete);
                setAnchorEl(null);
                setProjectToDelete(null);
              }}
              sx={{ mr: 1 }}
            >
              Yes
            </Button>

            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setProjectToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default RoleManagementTable;
